// SMAU10Arabic.js
// Arabic version of SMA U10 test for children under 10 years
// Following the same UI structure as PAT-10 Arabic

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SMA.css";
import { BASE_URL } from "../../Config";
import logoImage from "../../../resources/1000028180.jpeg";
import { FiMenu } from 'react-icons/fi';

const questions = {
  handling: [
    "أواصل المحاولة حتى لو غلطت في المباراة.",
    "أحب أتعلم مهارات جديدة حتى لو كانت صعبة.",
    "إذا خسرت ما أزعل لفترة طويلة.",
    "إذا كان الشيء صعب، أجرب مرة ثانية وما أستسلم.",
    "أشعر بالحماس ألعب حتى ضد خصوم أقوياء."
  ],
  focused: [
    "أسمع بعناية لما المدرب يعطيني تعليمات.",
    "أحاول أركز في المباراة وما أتشتت.",
    "أتذكر كلام المدرب وأستخدمه في المباراة.",
    "أبقى مركز حتى لو في ضجة أو تصفيق.",
    "إذا تشتت، أقدر أرجع وأركز بسرعة."
  ],
  confident: [
    "أؤمن أني أقدر ألعب بشكل جيد في رياضتي.",
    "أشعر بالسعادة والحماس لما ألعب.",
    "أثق في نفسي أني أبذل أفضل ما عندي في كل مباراة.",
    "حتى لو غلطت، أعرف أني أقدر أكون أفضل في المرة الجاية.",
    "أشعر بالقوة والاستعداد قبل ما تبدأ المباراة."
  ],
  nervousFeeling: [
    "أشعر بالتوتر قبل المباريات.",
    "أقلق إني أغلط في اللعب.",
    "يجيني إحساس \"فراشات في المعدة\" قبل اللعب.",
    "أحس بشد وتوتر في اللحظات المهمة.",
    "أخاف أخذل فريقي."
  ]
};

const sections = [
  { key: 'handling_challenges', name: 'التعامل مع التحديات', questions: questions.handling },
  { key: 'staying_focused', name: 'الحفاظ على التركيز', questions: questions.focused },
  { key: 'confidence', name: 'الشعور بالثقة', questions: questions.confident },
  { key: 'nervous_feelings', name: 'الشعور بالتوتر', questions: questions.nervousFeeling }
];

const answerLabels = ["لا، مش كثير", "أحيانًا", "نعم، كثير"];

const SMAU10Arabic = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [alertMsg, setAlertMsg] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  const handleSelect = (sectionKey, questionIndex, rating) => {
    setResponses(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [questionIndex]: rating
      }
    }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    const currentSectionData = sections[currentSection];
    const sectionResponses = responses[currentSectionData.key] || {};

    for (let i = 0; i < currentSectionData.questions.length; i++) {
      if (!sectionResponses[i]) {
        setAlertMsg("يرجى الإجابة على جميع الأسئلة قبل المتابعة.");
        setShowAlert(true);
        return;
      }
    }
    setCurrentSection((prev) => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentSectionData = sections[currentSection];
    const sectionResponses = responses[currentSectionData.key] || {};

    for (let i = 0; i < currentSectionData.questions.length; i++) {
      if (!sectionResponses[i]) {
        setAlertMsg("يرجى الإجابة على جميع الأسئلة قبل المتابعة.");
        setShowAlert(true);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const groupedResponses = {};
      sections.forEach(section => {
        const sectionResponses = responses[section.key] || {};
        groupedResponses[section.key] = section.questions.map((_, index) =>
          sectionResponses[index] || 0
        );
      });

      const payload = {
        test_type: "SMA-U10-AR",
        responses: groupedResponses,
      };

      const token = localStorage.getItem("accessToken");
      console.log("SMA U10 Arabic - Submitting payload:", payload);
      console.log("SMA U10 Arabic - Token:", token);
      
      const response = await fetch(`${BASE_URL}/api/test/submit_test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      console.log("SMA U10 Arabic - Response status:", response.status);
      console.log("SMA U10 Arabic - Response ok:", response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log("SMA U10 Arabic - Success response:", responseData);
        localStorage.setItem("smaU10", JSON.stringify(groupedResponses));
        navigate("/test-summary");
      } else {
        const errorData = await response.json();
        console.log("SMA U10 Arabic - Error response:", errorData);
        setAlertMsg(errorData.message || "حدث خطأ أثناء إرسال الاختبار");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error submitting test:", error);
      setAlertMsg("حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى.");
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/sport-mental-tests");
  };

  const handleStartTest = () => {
    setShowInstructions(false);
  };

  return (
    <div
      style={
        isMobile
          ? {
              minHeight: '100vh',
              background: `linear-gradient(to top left, #2AA7A1 -25.98%, #E0F7F4 66.42%)`,
              fontFamily: 'Poppins, sans-serif',
            }
          : {
              minHeight: '100vh',
              background: `linear-gradient(to top left, #2AA7A1 -25.98%, #E0F7F4 66.42%)`,
              fontFamily: 'Poppins, sans-serif',
            }
      }
    >
      {/* Header */}
      <header
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          padding: "1rem 2rem",
          boxShadow: "0 2px 20px rgba(0,0,0,0.1)",
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src={logoImage}
              alt="Logo"
              style={{
                height: 40,
                width: 40,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                color: "#11998e",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              اختبار ذهني مخصص لفئة تحت 10 سنوات
            </h1>
          </div>
          <button
            onClick={() => navigate("/sport-mental-tests")}
            style={{
              background: "none",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "#11998e",
            }}
          >
            <FiMenu />
          </button>
        </div>
      </header>

      <div
        style={
          isMobile
            ? {
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                marginTop: 40,
                marginBottom: '110px',
                paddingBottom: '20px',
              }
            : {
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                marginTop: 40,
                marginBottom: 80,
                paddingBottom: '20px',
              }
        }
      >
        <div
          style={
            isMobile
              ? {
                  background: '#fff',
                  borderRadius: '1.2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  width: '100vw',
                  maxWidth: '98vw',
                  minWidth: 0,
                  padding: '1.2rem 0.7rem 1.5rem 0.7rem',
                  boxSizing: 'border-box',
                  margin: 0,
                  marginBottom: '110px',
                  minHeight: '90vh',
                  direction: 'rtl',
                  fontFamily: 'Poppins, sans-serif',
                }
              : {
                  background: '#fff',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  width: 1028,
                  minHeight: 970,
                  padding: '2rem',
                  direction: 'rtl',
                  fontFamily: 'Poppins, sans-serif',
                }
          }
        >
          {/* Test Title */}
          <h1 className="logo-title" style={{ color: ' rgba(52, 52, 52, 1)' }}>
            اختبار ذهني مخصص لفئة تحت 10 سنوات (SMA U10 Arabic)
          </h1>

          {/* Instructions Modal */}
          {showInstructions ? (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: 20,
              }}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: 12,
                  padding: 30,
                  maxWidth: 500,
                  width: "100%",
                  textAlign: "center",
                  direction: "rtl",
                }}
              >
                <h2 style={{ color: "#11998e", marginBottom: 20 }}>
                  تعليمات الاختبار
                </h2>
                <p style={{ marginBottom: 20, lineHeight: 1.6 }}>
                  هذا الاختبار مخصص للأطفال تحت سن 10 سنوات. يرجى قراءة كل سؤال بعناية واختيار الإجابة التي تناسبك أكثر.
                </p>
                <p style={{ marginBottom: 30, lineHeight: 1.6 }}>
                  الاختبار مقسم إلى 4 أقسام: التعامل مع التحديات، الحفاظ على التركيز، الشعور بالثقة، والشعور بالتوتر.
                </p>
                <button
                  onClick={handleStartTest}
                  style={{
                    background: "#11998e",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: 8,
                    fontSize: 16,
                    cursor: "pointer",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  متابعة
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Step-based Progress Bar */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: '100%',
                  margin: '16px 0 8px 0',
                }}
              >
                {Array.from({ length: sections.length }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      height: 10,
                      borderRadius: 6,
                      background: idx <= currentSection ? '#2AA7A1' : '#e0e0e0',
                      transition: 'background 0.3s',
                    }}
                  />
                ))}
              </div>
              {/* Section progress indicator */}
              <div
                className="progress-text"
                style={{ marginBottom: 16, color: 'rgba(52, 52, 52, 1)' }}
              >
                القسم {currentSection + 1} من {sections.length}
              </div>
              {/* Section Title */}
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: '#11998e',
                  textAlign: 'center',
                  marginBottom: 8,
                }}
              >
                {currentSectionData.name}
              </div>

              {/* Questions */}
              {currentSectionData.questions.map((question, questionIndex) => (
                <div
                  key={questionIndex}
                  style={
                    isMobile
                      ? {
                          background: '#fff',
                          borderRadius: '1.2rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          marginBottom: 12,
                          padding: '1.2rem 0.7rem 1.5rem 0.7rem',
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          fontFamily: 'Poppins, sans-serif',
                        }
                      : {
                          background: '#fff',
                          borderRadius: '1.2rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          marginBottom: 16,
                          padding: '1.5rem 1.2rem 1.8rem 1.2rem',
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          fontFamily: 'Poppins, sans-serif',
                        }
                  }
                >
                  <div className="question-text">
                    {questionIndex + 1}. {question}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      gap: 12,
                      marginTop: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    {answerLabels.map((label, optionIndex) => (
                      <label
                        key={optionIndex}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          fontSize: isMobile ? 14 : 16,
                          color: "#333",
                          fontFamily: "Poppins, sans-serif",
                          padding: isMobile ? "8px 0" : "8px 12px",
                          borderRadius: 8,
                          transition: "background-color 0.2s",
                        }}
                      >
                        <input
                          type="radio"
                          name={`question-${questionIndex}`}
                          value={optionIndex + 1}
                          checked={responses[currentSectionData.key]?.[questionIndex] === optionIndex + 1}
                          onChange={() => handleSelect(currentSectionData.key, questionIndex, optionIndex + 1)}
                          style={{
                            margin: 0,
                            accentColor: "#11998e",
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  onClick={handleCancel}
                  style={{
                    background: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 16,
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={isLastSection ? handleSubmit : handleNext}
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? '#ccc' : '#11998e',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 8,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: 16,
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {isSubmitting ? 'جاري الإرسال...' : (isLastSection ? 'إرسال' : 'التالي')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      {showAlert && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 12,
              padding: 30,
              maxWidth: 400,
              width: "100%",
              textAlign: "center",
              direction: "rtl",
            }}
          >
            <h3 style={{ color: "#e74c3c", marginBottom: 20 }}>
              تنبيه
            </h3>
            <p style={{ marginBottom: 30, lineHeight: 1.6 }}>
              {alertMsg}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              style={{
                background: "#11998e",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: 8,
                fontSize: 16,
                cursor: "pointer",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              موافق
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMAU10Arabic;
